import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Plus, Trash2, ShieldCheck, Laptop, Tablet, Watch, Headphones, Camera, Gamepad2, Monitor, Package } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { photoSrc } from "@/lib/uploads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUploader from "@/components/PhotoUploader";
import DeviceModelPicker from "@/components/DeviceModelPicker";
import { DEVICES } from "@/constants/testIds";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  phone: Smartphone,
  laptop: Laptop,
  tablet: Tablet,
  smartwatch: Watch,
  headphones: Headphones,
  camera: Camera,
  gaming: Gamepad2,
  tv: Monitor,
  other: Package,
};

function CategoryIcon({ code }) {
  const Icon = CATEGORY_ICONS[code] || Smartphone;
  return <Icon className="w-6 h-6 text-muted-foreground" />;
}

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/devices");
    setDevices(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      const payload = {
        gadget_category: form.gadget_category || null,
        category_type: form.category_type || null,
        brand: form.brand,
        series: form.series || null,
        model: form.model,
        model_numbers: form.model_numbers || null,
        release_year: form.release_year ? parseInt(form.release_year, 10) : null,
        imei: form.imei,
        purchase_date: form.purchase_date || null,
        value_gbp: form.value_gbp ? parseFloat(form.value_gbp) : null,
        photo: form.photo || null,
        color: form.color || null,
        condition: "good",
      };
      await api.post("/devices", payload);
      toast.success("Gadget registered");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this gadget?")) return;
    await api.delete(`/devices/${id}`);
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-secondary">Your gadgets</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight">Registered gadgets</h1>
          <p className="mt-2 text-muted-foreground text-sm">Register every phone, laptop, tablet or gadget you'd like to protect.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={DEVICES.addBtn} className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-2" /> Register a gadget
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display font-light text-2xl">Register a gadget</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <CatalogCascade form={form} setForm={setForm} />
              <div>
                <Label className="text-xs">Serial / IMEI</Label>
                <Input required value={form.imei} onChange={e => setForm({...form, imei: e.target.value})} data-testid={DEVICES.imei} placeholder="Serial number or IMEI" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Purchase date</Label>
                  <Input type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})} data-testid={DEVICES.purchaseDate} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs">Value (£) <span className="text-muted-foreground">— override</span></Label>
                  <Input type="number" step="0.01" value={form.value_gbp} onChange={e => setForm({...form, value_gbp: e.target.value})} data-testid={DEVICES.value} placeholder="899.00" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Photo</Label>
                <div className="mt-1.5">
                  <PhotoUploader
                    value={form.photo}
                    onChange={(v) => setForm({ ...form, photo: v })}
                    purpose="device"
                    multiple={false}
                    testIdPrefix="device-photo"
                  />
                </div>
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <DialogFooter>
                <Button type="submit" data-testid={DEVICES.submit} disabled={saving}>{saving ? "Saving…" : "Register"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div data-testid={DEVICES.list} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {devices.length === 0 && (
          <div className="col-span-full text-center py-16 bg-card border border-dashed border-border rounded-2xl">
            <Smartphone className="w-12 h-12 mx-auto text-muted-foreground/40" />
            <div className="mt-4 font-display text-xl">No gadgets yet</div>
            <p className="text-sm text-muted-foreground mt-1">Register your first gadget to activate protection.</p>
          </div>
        )}
        {devices.map(d => (
          <div key={d.id} data-testid={DEVICES.deviceCard(d.id)} className="bg-card border border-border rounded-2xl p-5 hover:-translate-y-1 transition-transform">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center overflow-hidden shrink-0">
                {d.photo ? <img src={photoSrc(d.photo, "thumb")} alt={d.model} className="w-full h-full object-cover" loading="lazy" /> : <CategoryIcon code={d.gadget_category} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-medium text-lg truncate">{d.brand} {d.model}</div>
                <div className="text-xs text-muted-foreground mt-0.5 font-mono">SN ••{d.imei.slice(-6)}</div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {d.gadget_category && (
                    <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-secondary/15 text-secondary capitalize">{d.gadget_category}</span>
                  )}
                  <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${d.verified ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                    <ShieldCheck className="w-3 h-3 inline mr-1" />
                    {d.verified ? "Verified" : "Pending"}
                  </span>
                  {d.value_gbp && <span className="text-[10px] overline text-muted-foreground">£{d.value_gbp}</span>}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Link to="/app/claims/new" state={{ deviceId: d.id }} className="flex-1">
                <Button variant="outline" size="sm" className="w-full">File a claim</Button>
              </Link>
              <Button size="sm" variant="ghost" onClick={() => del(d.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function emptyForm() {
  return {
    gadget_category: "", category_type: "", brand: "", series: "",
    model: "", model_numbers: "", release_year: "",
    imei: "", purchase_date: "", value_gbp: "", photo: "", color: "",
  };
}

/**
 * CatalogCascade — 5-level filter over the device_model master:
 *   Gadget Category → Category Type → Manufacturer → Series → Model
 * Picking a model auto-fills Launch Year and Price (£). Any filter is
 * optional; the next dropdown narrows via `distinct` queries over the
 * 10k-row register. Any filter also narrows every OTHER filter (two-way),
 * so Brand first → Category list shrinks to that brand's gadgets.
 */
function CatalogCascade({ form, setForm }) {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [serieses, setSerieses] = useState([]);

  // Build the "narrow all other dropdowns" query — everything that is set EXCEPT
  // the dropdown we're populating.
  const buildQ = useCallback((omit) => {
    const q = {};
    if (form.gadget_category && omit !== "category") q.category = form.gadget_category;
    if (form.category_type && omit !== "category_type") q.category_type = form.category_type;
    if (form.brand && omit !== "brand") q.brand = form.brand;
    if (form.series && omit !== "series") q.series = form.series;
    return q;
  }, [form.gadget_category, form.category_type, form.brand, form.series]);

  useEffect(() => {
    api.get("/masters/lookup/gadget_category").then((r) => {
      const unsorted = r.data || [];
      setCategories(unsorted.sort((a, b) => (a.data?.order || 99) - (b.data?.order || 99)));
    }).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    api.get("/masters/lookup/device_model", { params: { ...buildQ("category_type"), distinct: "category_type" } })
      .then((r) => setTypes(r.data || []))
      .catch(() => setTypes([]));
  }, [buildQ]);

  useEffect(() => {
    api.get("/masters/lookup/device_model", { params: { ...buildQ("brand"), distinct: "brand" } })
      .then((r) => setBrands(r.data || []))
      .catch(() => setBrands([]));
  }, [buildQ]);

  useEffect(() => {
    api.get("/masters/lookup/device_model", { params: { ...buildQ("series"), distinct: "series" } })
      .then((r) => setSerieses(r.data || []))
      .catch(() => setSerieses([]));
  }, [buildQ]);

  // Filter the Gadget Category list down to only categories that still have
  // devices under the current type/brand/series selections.
  const [validCategoryCodes, setValidCategoryCodes] = useState(null);
  useEffect(() => {
    if (!form.category_type && !form.brand && !form.series) {
      setValidCategoryCodes(null);
      return;
    }
    api.get("/masters/lookup/device_model", { params: { ...buildQ("category"), distinct: "gadget_category" } })
      .then((r) => setValidCategoryCodes(r.data || []))
      .catch(() => setValidCategoryCodes(null));
  }, [buildQ, form.category_type, form.brand, form.series]);

  const visibleCategories = validCategoryCodes
    ? categories.filter((c) => validCategoryCodes.includes(c.data?.code))
    : categories;

  const onPickModel = (row) => {
    const d = row.data || {};
    setForm((f) => ({
      ...f,
      model: row.label,
      model_numbers: d.model_numbers ? String(d.model_numbers).split(",")[0].trim() : "",
      release_year: d.release_year ? String(d.release_year) : f.release_year,
      value_gbp: d.price_gbp && !f.value_gbp ? String(Math.round(d.price_gbp)) : f.value_gbp,
      // Backfill any cascade field left blank so the record is complete.
      gadget_category: f.gadget_category || d.gadget_category || "",
      category_type: f.category_type || d.category_type || "",
      brand: f.brand || d.brand || "",
      series: f.series || d.series || "",
    }));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CascadeSelect
          label="Device Category" testId="device-category" placeholder="Any category"
          value={form.gadget_category}
          options={visibleCategories.map((c) => ({ value: c.data?.code || c.label.toLowerCase(), label: c.label }))}
          onChange={(v) => setForm({ ...form, gadget_category: v, category_type: "", series: "", model: "" })}
        />
        <CascadeSelect
          label="Category Type" testId="device-type" placeholder="Any type"
          value={form.category_type}
          options={types.map((t) => ({ value: t, label: t }))}
          onChange={(v) => setForm({ ...form, category_type: v, series: "", model: "" })}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CascadeSelect
          label="Manufacturer" testId="device-brand" placeholder="Any brand"
          value={form.brand}
          options={brands.map((b) => ({ value: b, label: b }))}
          onChange={(v) => setForm({ ...form, brand: v, series: "", model: "" })}
        />
        <CascadeSelect
          label="Series" testId="device-series" placeholder="Any series"
          value={form.series}
          options={serieses.map((s) => ({ value: s, label: s }))}
          onChange={(v) => setForm({ ...form, series: v, model: "" })}
        />
      </div>
      <div>
        <Label className="text-xs">Model name / number</Label>
        <DeviceModelPicker
          category={form.gadget_category}
          categoryType={form.category_type}
          brand={form.brand}
          series={form.series}
          value={form.model}
          onPick={onPickModel}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Launch year</Label>
          <Input
            type="number"
            value={form.release_year}
            onChange={(e) => setForm({ ...form, release_year: e.target.value })}
            data-testid="device-release-year"
            placeholder="e.g. 2024"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="text-xs">Model code <span className="text-muted-foreground">— auto</span></Label>
          <Input
            value={form.model_numbers}
            onChange={(e) => setForm({ ...form, model_numbers: e.target.value })}
            data-testid="device-model-number"
            placeholder="e.g. A3102"
            className="mt-1.5"
          />
        </div>
      </div>
    </div>
  );
}

function CascadeSelect({ label, value, options, onChange, placeholder, testId }) {
  const CLEAR = "__clear__";
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select
        value={value || ""}
        onValueChange={(v) => onChange(v === CLEAR ? "" : v)}
      >
        <SelectTrigger data-testid={`${testId}-select`} className="mt-1.5">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {value && (
            <SelectItem key={CLEAR} value={CLEAR} data-testid={`${testId}-opt-clear`} className="text-muted-foreground italic">
              — Clear —
            </SelectItem>
          )}
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testId}-opt-${o.value}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
